/**
 * AttachmentViewer — slide-in right panel for viewing attachments inline.
 *
 * Supports:
 *  - Images (jpg, png, gif, webp, svg, bmp) → inline <img>
 *  - PDF → embedded <iframe>
 *  - Text/CSV/SQL/JSON/XML → fetched and rendered as syntax-styled <pre>
 *  - Excel/Word/PPT/Zip/Video → unsupported-for-inline, shows icon + download
 *
 * Includes prev/next navigation and a download button in the header.
 */
import { useCallback, useEffect, useState, type JSX } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileArchive,
  FileCode,
  FileSpreadsheet,
  FileText,
  FileType,
  Film,
  Image as ImageIcon,
  Presentation,
  X,
} from 'lucide-react'
import type { MessageAttachment } from '../../../modules/conversations/types.js'
import { useSignedAttachmentUrl } from '../../../hooks/useSignedAttachmentUrl.js'
import { R } from './primitives.js'

function formatSize(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

type AttachmentKind = 'image' | 'pdf' | 'text' | 'csv' | 'code' | 'video' | 'unsupported'

function classifyAttachment(mimeType: string, filename: string): AttachmentKind {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  const mime = mimeType.toLowerCase()

  if (mime.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico'].includes(ext)) return 'image'
  if (mime === 'application/pdf' || ext === 'pdf') return 'pdf'
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'wmv', 'flv', 'mkv', 'webm', 'mpeg', 'mpg', 'm4v'].includes(ext)) return 'video'
  if (ext === 'csv' || mime === 'text/csv') return 'csv'
  if (['sql', 'json', 'xml', 'html', 'htm', 'js', 'ts', 'py', 'java', 'c', 'cpp', 'go', 'rb', 'php', 'sh', 'yaml', 'yml', 'toml', 'ini', 'cfg', 'log'].includes(ext)) return 'code'
  if (mime.startsWith('text/') || ['txt', 'md', 'rst', 'csv'].includes(ext)) return 'text'
  return 'unsupported'
}

function iconForKind(kind: AttachmentKind): typeof FileText {
  switch (kind) {
    case 'image': return ImageIcon
    case 'pdf': return FileType
    case 'video': return Film
    case 'csv': return FileSpreadsheet
    case 'code': return FileCode
    case 'text': return FileText
    default: return FileText
  }
}

function iconForUnsupported(filename: string): typeof FileText {
  const ext = filename.split('.').pop()?.toLowerCase() ?? ''
  if (['xls', 'xlsx', 'ods'].includes(ext)) return FileSpreadsheet
  if (['doc', 'docx', 'odt'].includes(ext)) return FileText
  if (['ppt', 'pptx', 'odp'].includes(ext)) return Presentation
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return FileArchive
  return FileText
}

export interface AttachmentViewerProps {
  attachments: (MessageAttachment & { url?: string })[]
  index: number
  open: boolean
  onClose: () => void
  onNavigate: (index: number) => void
}

export default function AttachmentViewer({
  attachments,
  index,
  open,
  onClose,
  onNavigate,
}: AttachmentViewerProps): JSX.Element | null {
  const [textContent, setTextContent] = useState<string | null>(null)
  const [textLoading, setTextLoading] = useState(false)
  const [textError, setTextError] = useState<string | null>(null)

  const attachment = attachments[index]
  const kind = attachment ? classifyAttachment(attachment.mimeType, attachment.filename) : 'unsupported'
  const attachmentId = open && attachment && !attachment.url ? attachment.id : null
  const { url: signedViewUrl } = useSignedAttachmentUrl(attachmentId)
  const viewUrl = attachment?.url ?? signedViewUrl

  const goPrev = useCallback(() => {
    if (index > 0) onNavigate(index - 1)
  }, [index, onNavigate])

  const goNext = useCallback(() => {
    if (index < attachments.length - 1) onNavigate(index + 1)
  }, [index, attachments.length, onNavigate])

  // Keyboard navigation
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose, goPrev, goNext])

  // Fetch text content for text/csv/code types
  useEffect(() => {
    if (!open || !attachment) {
      setTextContent(null)
      setTextError(null)
      return
    }
    if (kind !== 'text' && kind !== 'csv' && kind !== 'code') {
      setTextContent(null)
      return
    }
    setTextLoading(true)
    setTextError(null)
    setTextContent(null)
    let cancelled = false
    fetch(viewUrl ?? `/api/attachments/${attachment.id}`, { credentials: 'include' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.text()
      })
      .then((text) => {
        if (!cancelled) {
          setTextContent(text)
          setTextLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTextError(err instanceof Error ? err.message : 'Failed to load')
          setTextLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [open, attachment, kind])

  if (!open || !attachment) return null

  const downloadUrl = viewUrl ?? (attachment ? `/api/attachments/${attachment.id}?download=1` : '')
  const canPrev = index > 0
  const canNext = index < attachments.length - 1
  const Icon = kind === 'unsupported' ? iconForUnsupported(attachment.filename) : iconForKind(kind)

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-sm" onClick={onClose} />

      {/* Slide-in panel from right */}
      <div className="animate-slide-in-right fixed right-0 top-0 z-[61] flex h-full w-[60%] min-w-[500px] flex-col bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <Icon className="h-5 w-5 shrink-0 text-slate-400" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-slate-800">{attachment.filename}</p>
              <p className="text-[11px] text-slate-400">
                {attachment.mimeType}
                {attachment.sizeBytes > 0 && ` · ${formatSize(attachment.sizeBytes)}`}
                {attachments.length > 1 && ` · ${index + 1} of ${attachments.length}`}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            {/* Prev/Next */}
            {attachments.length > 1 && (
              <div className="mr-1 flex items-center gap-0.5">
                <button
                  type="button"
                  disabled={!canPrev}
                  onClick={goPrev}
                  className={`${R} p-1.5 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent`}
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={!canNext}
                  onClick={goNext}
                  className={`${R} p-1.5 text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent`}
                  title="Next (→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Download */}
            <a
              href={downloadUrl}
              download={attachment.filename}
              className={`${R} flex items-center gap-1.5 bg-[#3366FF] px-3 py-1.5 text-xs font-semibold text-[#fff] transition-colors hover:bg-[#2952CC]`}
            >
              <Download className="h-3.5 w-3.5 text-[#fff]" />
              <span className='text-[#fff]'>Download</span>
            </a>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              className={`${R} p-1.5 text-slate-500 transition-colors hover:bg-slate-100`}
              title="Close (Esc)"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content area */}
        <div className="min-h-0 flex-1 overflow-auto bg-slate-50">
          {kind === 'image' && (
            <div className="flex h-full items-center justify-center p-6">
              <img
                src={viewUrl ?? ''}
                alt={attachment.filename}
                className="max-h-full max-w-full rounded-[3px] object-contain shadow-md"
              />
            </div>
          )}

          {kind === 'pdf' && (
            <iframe
              src={viewUrl ?? ''}
              title={attachment.filename}
              className="h-full w-full border-0"
            />
          )}

          {kind === 'video' && (
            <div className="flex h-full items-center justify-center p-6">
              <video
                src={viewUrl ?? ''}
                controls
                className="max-h-full max-w-full rounded-[3px] shadow-md"
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}

          {(kind === 'text' || kind === 'csv' || kind === 'code') && (
            <div className="h-full overflow-auto bg-[#1e1e2e] p-4">
              {textLoading && (
                <div className="flex items-center justify-center py-12 text-slate-400">
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-600 border-t-slate-300" />
                  <span className="ml-2 text-sm">Loading…</span>
                </div>
              )}
              {textError && (
                <div className="py-12 text-center text-sm text-red-400">
                  Failed to load: {textError}
                </div>
              )}
              {textContent !== null && !textLoading && !textError && (
                <>
                  {kind === 'csv' ? (
                    <CsvView content={textContent} />
                  ) : (
                    <pre className="overflow-auto text-xs leading-relaxed text-slate-200">
                      <code>{textContent}</code>
                    </pre>
                  )}
                </>
              )}
            </div>
          )}

          {kind === 'unsupported' && (
            <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-[3px] bg-slate-100">
                <Icon className="h-10 w-10 text-slate-300" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">{attachment.filename}</p>
                <p className="mt-1 text-xs text-slate-400">
                  {attachment.mimeType} · {formatSize(attachment.sizeBytes)}
                </p>
                <p className="mt-3 text-xs text-slate-400">
                  This file type can&apos;t be previewed inline.
                </p>
              </div>
              <a
                href={downloadUrl}
                download={attachment.filename}
                className={`${R} flex items-center gap-2 bg-[#3366FF] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#2952CC]`}
              >
                <Download className="h-4 w-4" />
                Download File
              </a>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/* ── CSV table renderer ── */
function CsvView({ content }: { content: string }): JSX.Element {
  const rows = content.split('\n').filter((l) => l.trim())
  const parsed = rows.map((row) => parseCsvRow(row))
  const maxCols = Math.max(...parsed.map((r) => r.length), 1)

  return (
    <div className="overflow-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="bg-[#2a2a3e]">
            {Array.from({ length: maxCols }).map((_, i) => (
              <th key={i} className="border border-[#3a3a4e] px-3 py-1.5 text-left font-semibold text-slate-300">
                {parsed[0]?.[i] ?? ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {parsed.slice(1).map((row, ri) => (
            <tr key={ri} className="hover:bg-[#2a2a3e]">
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td key={ci} className="border border-[#3a3a4e] px-3 py-1.5 text-slate-200">
                  {row[ci] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function parseCsvRow(row: string): string[] {
  const cells: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < row.length; i++) {
    const ch = row[i]
    if (ch === '"' && row[i + 1] === '"') {
      current += '"'
      i++
    } else if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      cells.push(current)
      current = ''
    } else {
      current += ch
    }
  }
  cells.push(current)
  return cells
}
